import { Action, State, StateContext } from '@ngxs/store';
import { carriesAValue } from '../../../utils/an-answer-that-carries-its-value';
import {
  CreateGroupAction,
  SelectSportIdCreateGroupAction,
  SetBackgroundImgCreateGroupAction,
  SetGroupDescriptionCreateGroupAction,
  SetGroupLocationCreateGroupAction,
  SetWebsiteUrlCreateGroupAction,
  SetHandiFriendlyCreateGroupAction,
  SetPrivacyCreateGroupAction,
  ResetCreateGroupStateAction,
  SetRightCreateActivityCreateGroupAction,
  SetTitleCreateGroupAction,
  SetParentGroupIdCreateGroupAction,
  SetActivitySharingCreateGroupAction,
} from './create-group.actions';
import { CreateGroupDto, CreateGroupRequestParams, GroupsService } from '@smatchy/sdk';
import { GeoLocality, GeoPosition } from '@smatchy/core';
import { catchError, tap } from 'rxjs';
import { aFailedCallIsReportedAndStops } from '../../../utils/a-failed-call-is-reported-and-stops';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { buildLocationStorageName } from '../../../utils/resolve-admin-level.util';

export interface CreateGroupStateModel {
  bgUrl: string;
  location: {
    /** Neither is known before somebody picks a place, which is what the defaults set. */
    locality?: GeoLocality;
    position?: GeoPosition;
  };
  description: string;
  websiteUrl: string;
  handiFriendly: boolean;
  loading: boolean;
  title: string;
  sportIdSelected: string;
  sportKeyIdSelected: string;
  /**
   * SMA-99: every sport chosen, in the order they were chosen.
   *
   * `sportIdSelected` above stays and holds the FIRST of these, because it is
   * what gives the group its cover picture and what an older API reads. The two
   * are written together and never separately.
   */
  sportIdsSelected: string[];
  privacy: CreateGroupDto.PrivacyEnum;
  rightCreateActivity: CreateGroupDto.RightCreateActivityEnum;
  activitySharing: boolean;
  parentGroupId: string;
}

const defaults: CreateGroupStateModel = {
  bgUrl: '',
  location: {
    locality: undefined,
    position: undefined,
  },
  description: '',
  websiteUrl: '',
  handiFriendly: false,
  loading: false,
  sportIdSelected: '',
  sportKeyIdSelected: '',
  sportIdsSelected: [],
  title: '',
  privacy: CreateGroupDto.PrivacyEnum.Public,
  rightCreateActivity: CreateGroupDto.RightCreateActivityEnum.AllMembers,
  activitySharing: false,
  parentGroupId: '',
};

@State<CreateGroupStateModel>({
  name: 'createGroup',
  defaults,
})
@Injectable()
export class CreateGroupState {
  constructor(private readonly _groupsService: GroupsService, private readonly _router: Router) {}

  @Action(CreateGroupAction)
  async createGroupAction({ getState, patchState }: StateContext<CreateGroupStateModel>) {
    const state = getState();

    if (!this.isValid(state)) {
      console.error('cannot create group, invalid state');
      return;
    }

    patchState({
      loading: true,
    });

    const bodyForm: CreateGroupRequestParams = {
      createGroupDto: {
        rightCreateActivity: state.rightCreateActivity,
        allowActivitySharing: state.activitySharing,
        handiFriendly: state.handiFriendly,
        backgroundUrl: state.bgUrl,
        description: state.description,
        name: state.title,
        sportId: state.sportIdSelected,
        // The list, falling back to the single sport for the same reason
        // `isValid` accepts it: a caller that set only that field means one
        // sport, not an unknown list.
        sportIds: state.sportIdsSelected?.length ? state.sportIdsSelected : [state.sportIdSelected].filter(Boolean),
        privacy: state.privacy,
      },
    };

    const websiteUrl = state.websiteUrl.trim();
    if (websiteUrl) {
      bodyForm.createGroupDto.websiteUrl = websiteUrl;
    }

    if (state.location.position) {
      const locality = state.location.locality;
      bodyForm['createGroupDto']['location'] = {
        lat: state.location.position.lat,
        lon: state.location.position.lng,
        name: buildLocationStorageName(locality?.hierarchy, locality?.formattedAddress || locality?.suggestionDetail),
        placeId: locality?.placeId,
        hierarchy: locality?.hierarchy,
      };
    }

    if (state.parentGroupId) {
      bodyForm.createGroupDto.parentGroupId = state.parentGroupId;
    }

    return this._groupsService.createGroup(bodyForm).pipe(
      catchError((err) => {
        patchState({ loading: false });
        return aFailedCallIsReportedAndStops(err);
      }),
      tap((elem) => {
        patchState({
          loading: false,
        });
        // The success screen names the group it just created, and read it with
        // no guard: an answer carrying nothing threw on the way there, after
        // the group may well have been created. Nothing is navigated to in that
        // case, which leaves the person on the form rather than on a screen
        // that cannot draw itself.
        if (!carriesAValue(elem)) {
          return;
        }
        this._router.navigate(['/home/create-group/success'], {
          queryParams: {
            title: elem.value.group.name,
            _id: elem.value.group._id,
          },
        });
      }),
    );
  }

  @Action(SetTitleCreateGroupAction)
  async setTitleCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SetTitleCreateGroupAction,
  ) {
    patchState({
      title: payload,
    });
  }

  @Action(SetGroupDescriptionCreateGroupAction)
  async SetGroupDescriptionCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SetGroupDescriptionCreateGroupAction,
  ) {
    patchState({
      description: payload,
    });
  }

  @Action(SetWebsiteUrlCreateGroupAction)
  async setWebsiteUrlCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SetWebsiteUrlCreateGroupAction,
  ) {
    patchState({
      websiteUrl: payload,
    });
  }

  @Action(SetHandiFriendlyCreateGroupAction)
  async setHandiFriendlyCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SetHandiFriendlyCreateGroupAction,
  ) {
    patchState({
      handiFriendly: payload === true,
    });
  }

  @Action(SetGroupLocationCreateGroupAction)
  async SetGroupLocationCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SetGroupLocationCreateGroupAction,
  ) {
    patchState({
      location: payload,
    });
  }

  @Action(SetBackgroundImgCreateGroupAction)
  async setBackgroundImgCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SetBackgroundImgCreateGroupAction,
  ) {
    patchState({
      bgUrl: payload,
    });
  }

  @Action(SetPrivacyCreateGroupAction)
  async SetPrivacyCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SetPrivacyCreateGroupAction,
  ) {
    patchState({
      privacy: payload,
    });
  }

  @Action(SetRightCreateActivityCreateGroupAction)
  async SetRightCreateActivityCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SetRightCreateActivityCreateGroupAction,
  ) {
    patchState({
      rightCreateActivity: payload,
    });
  }

  @Action(SelectSportIdCreateGroupAction)
  async selectSportIdCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SelectSportIdCreateGroupAction,
  ) {
    patchState({
      sportIdSelected: payload.sportId,
      sportKeyIdSelected: payload.sportKeyId,
      // The list and the single field are written together. A caller that sends
      // one sport is describing a group with one sport, not a group whose list
      // is unknown, and leaving the list behind here is how the two would drift.
      sportIdsSelected: payload.sportIds ?? [payload.sportId].filter(Boolean),
    });
  }

  @Action(SetParentGroupIdCreateGroupAction)
  async setParentGroupIdCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SetParentGroupIdCreateGroupAction,
  ) {
    patchState({
      parentGroupId: payload ?? '',
    });
  }

  @Action(SetActivitySharingCreateGroupAction)
  async setActivitySharingCreateGroupAction(
    { patchState }: StateContext<CreateGroupStateModel>,
    { payload }: SetActivitySharingCreateGroupAction,
  ) {
    patchState({
      activitySharing: payload ?? false,
    });
  }

  @Action(ResetCreateGroupStateAction)
  resetCreateGroupStateAction({ setState }: StateContext<CreateGroupStateModel>) {
    setState({ ...defaults });
  }

  private isValid(state: CreateGroupStateModel): boolean {
    // EITHER SHAPE IS VALID, and the single field is not a leftover. A state
    // carrying only `sportIdSelected` describes a group with one sport, and
    // callers that predate the list still build one: requiring the list made
    // them silently invalid, and on a state without the field at all it threw
    // before the group was ever created. Found by handi-friendly.spec, which
    // hands over exactly that state.
    const chosen = state.sportIdsSelected ?? [];
    const hasASport = chosen.length > 0 || state.sportIdSelected !== '';

    return state.title !== '' && hasASport && this.isWebsiteUrlValid(state.websiteUrl);
  }

  private isWebsiteUrlValid(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) return true;

    if (!/^https?:\/\//i.test(trimmed) || /\s/.test(trimmed)) {
      return false;
    }

    try {
      const url = new URL(trimmed);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }
}
